/**
 * Integrações › **Tiles do mapa** (W9-OPS-LIVE-MAP, D471, ADR-0089; owner decision 12 of wave 8: self-hosted tiles). One slot of
 * the credential vault holds the tile source the Admin map draws: an XYZ URL template with `{z}`, `{x}` and `{y}` (no query,
 * no credentials), the attribution to show, the zoom range and an on/off switch. There is NO key — the operator's browser
 * requests the tiles — and NO third-party provider in code: until a source is configured here the live map says so and
 * every list, filter and number keeps working. Composes `@vello/web-theme` only; the rule is `@vello/maps-ui` `tileSourceIssue`.
 */
import { type FormEvent, type ReactElement, useEffect, useId, useState } from 'react';
import type { MapTilesSlotView } from '@vello/api-client';
import { tileSourceIssue } from '@vello/maps-ui';
import { Badge, Button, CheckRow, Field, Notice, Panel, TextInput, FormGrid, Row, Text, Ink, Stack, FormStack } from '@vello/web-theme';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

export function MapTilesPanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail, v } = useVocab();
  const id = useId();
  const [slot, setSlot] = useState<MapTilesSlotView | undefined>(undefined);
  const [vault, setVault] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [urlTemplate, setUrlTemplate] = useState('');
  const [attribution, setAttribution] = useState('');
  const [minZoom, setMinZoom] = useState('0');
  const [maxZoom, setMaxZoom] = useState('18');
  const [msg, setMsg] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const fill = (s: MapTilesSlotView): void => {
    setSlot(s);
    setEnabled(s.enabled);
    if (s.source !== undefined) {
      setUrlTemplate(s.source.urlTemplate);
      setAttribution(s.source.attribution);
      setMinZoom(String(s.source.minZoom));
      setMaxZoom(String(s.source.maxZoom));
    }
  };

  useEffect(() => {
    let cancelled = false;
    void runtime.integrations.getMapTiles().then((res) => {
      if (cancelled) return;
      if (res.ok) {
        setVault(res.value.vault);
        fill(res.value.tiles);
      } else setMsg({ tone: 'danger', text: t('lmt.err.load', { code: fail(res.error) }) });
    });
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const input = { enabled, urlTemplate: urlTemplate.trim(), attribution: attribution.trim(), minZoom: Number(minZoom), maxZoom: Number(maxZoom) };
    if (enabled || input.urlTemplate !== '') {
      const issue = tileSourceIssue(input);
      if (issue !== undefined) {
        setMsg({ tone: 'danger', text: v('maps.tile_form_issue', issue) });
        return;
      }
    }
    setBusy(true);
    const res = await runtime.integrations.setMapTiles(input.urlTemplate === '' && !enabled ? { enabled } : input);
    setBusy(false);
    if (res.ok) {
      fill(res.value.tiles);
      setMsg({ tone: 'success', text: t('lmt.saved') });
    } else {
      const issue = res.error.reasonCode;
      setMsg({ tone: 'danger', text: issue !== undefined && issue.startsWith('TILE_') ? v('maps.tile_form_issue', issue) : t('lmt.err.save', { code: fail(res.error) }) });
    }
  };

  const status = slot?.status ?? 'NOT_CONFIGURED';
  const tone = status === 'CONFIGURED' ? 'success' : status === 'INVALID' ? 'danger' : 'neutral';

  return (
    <Panel><Stack gap={4}>
      <Row gap={3} align="center">
        <Text as="h2" role="title">{t('lmt.title')}</Text>
        <Badge tone={tone}>{v('map.tiles_status', status)}</Badge>
      </Row>
      <Ink as="p" tone="secondary">{t('lmt.hint')}</Ink>
      {!vault && (
        <div>
          <Notice tone="warning">{t('lmt.noVault')}</Notice>
        </div>
      )}
      <FormStack gap={3} onSubmit={(e) => void onSave(e)} aria-label={t('lmt.title')}>
        <FormGrid>
          <Field label={t('lmt.url')} htmlFor={`${id}-url`} hint={t('lmt.url.hint')}>
            <TextInput id={`${id}-url`} value={urlTemplate} onChange={(e) => setUrlTemplate(e.target.value)} spellCheck={false} autoComplete="off" />
          </Field>
          <Field label={t('lmt.attribution')} htmlFor={`${id}-attr`}>
            <TextInput id={`${id}-attr`} value={attribution} onChange={(e) => setAttribution(e.target.value)} />
          </Field>
          <Field label={t('lmt.minZoom')} htmlFor={`${id}-min`}>
            <TextInput id={`${id}-min`} className="tnum" inputMode="numeric" value={minZoom} onChange={(e) => setMinZoom(e.target.value)} />
          </Field>
          <Field label={t('lmt.maxZoom')} htmlFor={`${id}-max`}>
            <TextInput id={`${id}-max`} className="tnum" inputMode="numeric" value={maxZoom} onChange={(e) => setMaxZoom(e.target.value)} />
          </Field>
        </FormGrid>
        <CheckRow id={`${id}-enabled`} checked={enabled} onChange={setEnabled}>{t('lmt.enabled')}</CheckRow>
        {msg !== undefined && (
          <div>
            <Notice tone={msg.tone}>{msg.text}</Notice>
          </div>
        )}
        <div>
          <Button variant="secondary" type="submit" disabled={busy || !vault}>{t('lmt.save')}</Button>
        </div>
      </FormStack>
    </Stack></Panel>
  );
}
